// Runes state of the Providers tab: the computers the relay returned and one refresh state per computer.
import { failureKindForCode } from "@cyborg7/observability/failure";
import { reportKpi, reportUiFailure } from "$lib/report-failure.js";
import {
  refreshAllKpi,
  sortAccounts,
  switchAccountName,
  type RefreshOutcome,
} from "./provider-usage-format.js";
import type {
  OrgUsage,
  ProviderAccountSwitchResponse,
  ProviderUsageRefreshResponse,
  ProviderUsageResponse,
  SwitchFailure,
  UsageAccount,
  UsageComputer,
} from "./provider-usage-types.js";

export type RefreshState =
  | { kind: "idle" }
  | { kind: "refreshing" }
  | { kind: "updated"; at: number }
  | { kind: "no_answer" }
  | { kind: "offline" };

/** One switch per computer, for one account; its name stays known even if the row's key changes. */
export type SwitchState = {
  accountKey: string;
  name: string;
  provider: string;
  switcher: string;
} & (
  | { kind: "checking" | "switching" }
  | { kind: "confirm"; runningAgents: number }
  | { kind: "done"; runningAgents: number; writeUnknown: boolean; stillUsing?: string }
  | {
      kind: "failed";
      reason: SwitchFailure;
      active: string | null;
      answered: boolean;
      unchanged: boolean;
      confirmed: boolean;
    }
);

// Outside any one page: the settings layout swaps desktop and phone branches at the breakpoint, which
// remounts the page, and a switch in flight must land on whichever page is showing by then.
const SETTLED_KEEP_MS = 60_000;
const switchStates = $state<Record<string, SwitchState | undefined>>({});
const settledAt = new Map<string, number>();
const liveStates = new Set<ProviderUsageState>();
// The last computers each workspace showed: a remounted page (tab change, breakpoint swap) starts from them.
const lastKnown = new Map<string, { computers: UsageComputer[]; canManageOrgKeys: boolean }>();
// Bumped by sign-out: an answer to a request from before it belongs to nobody on this screen.
let signOutEpoch = 0;

export function clearSwitchStates(): void {
  signOutEpoch += 1;
  lastKnown.clear();
  for (const key of Object.keys(switchStates)) delete switchStates[key];
  settledAt.clear();
}

function forgetOldSwitches(now: number): void {
  for (const [key, at] of settledAt) {
    if (now - at < SETTLED_KEEP_MS) continue;
    delete switchStates[key];
    settledAt.delete(key);
  }
}

function switchFailureKind(err: unknown): "refusal" | undefined {
  const code = (err as { code?: unknown } | undefined)?.code; // cast: a wire error's optional code
  if (typeof code !== "string") return undefined;
  return failureKindForCode(code) === "refusal" ? "refusal" : undefined;
}

export interface ProviderUsageApi {
  fetchProviderUsage(p: {
    workspaceId: string;
    daemonId?: string;
    includeOrganization?: boolean;
    organizationOnly?: boolean;
  }): Promise<ProviderUsageResponse>;
  refreshProviderUsage(p: {
    workspaceId: string;
    daemonId: string;
  }): Promise<ProviderUsageRefreshResponse>;
  listProviders?(p: {
    workspaceId: string;
    daemonId: string;
  }): Promise<Array<{ id: string; label: string; available?: boolean }>>;
  switchProviderAccount(p: {
    workspaceId: string;
    daemonId: string;
    accountKey: string;
    confirmed: boolean;
  }): Promise<ProviderAccountSwitchResponse>;
}

const SOURCE = "provider_usage";
// The page clock: minute-granular texts ("in 12 min", "Limit reached") follow it without a reload.
const CLOCK_MS = 30_000;
// A failed reload tries once more after this, so a brief network drop does not leave the page empty.
const RELOAD_RETRY_MS = 5_000;
// Past this an unanswered read shows as failed and retries; the request stays open, so its answer still lands while newest.
const LOAD_VISIBLE_MS = 15_000;
// A check that comes straight back with a question never flashes "Switching…".
const SWITCHING_LABEL_AFTER_MS = 400;

// The relay answers a quiet daemon with daemon_timeout and a gone one with daemon_disconnected
// or "no daemon connected".
// A relay refusal (lost admin scope, not in the workspace) is its own outcome: no "didn't answer".
function outcomeOf(err: unknown): RefreshOutcome {
  const code = (err as { code?: unknown } | undefined)?.code;
  if (code === "daemon_timeout") return "no_answer";
  if (code === "daemon_disconnected") return "offline";
  if (typeof code === "string" && failureKindForCode(code) === "refusal") return "refused";
  return err instanceof Error && /no daemon connected/i.test(err.message) ? "offline" : "no_answer";
}

export class ProviderUsageState {
  computers = $state<UsageComputer[]>([]);
  organization = $state<OrgUsage[]>([]);
  canManageOrgKeys = $state(false);
  loaded = $state(false);
  /** The latest load failed: an empty list then means "unknown", not "gone". */
  loadFailed = $state(false);
  /** The organization read is its own request: a slow provider holds up this section only. */
  orgLoading = $state(false);
  orgFailed = $state(false);
  private loadSeq = 0;
  private orgSeq = 0;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private deadlineTimer: ReturnType<typeof setTimeout> | undefined;
  private orgRetryTimer: ReturnType<typeof setTimeout> | undefined;
  now = $state(Date.now());
  /** A "Refresh all" is still settling: its button stays disabled until the last computer answers. */
  refreshingAll = $state(false);
  private providers = $state<Record<string, Array<{ id: string; label: string }>>>({});
  private refreshStates = $state<Record<string, RefreshState>>({});
  private lastLoad: { daemonId?: string; includeOrganization?: boolean } = {};

  constructor(
    private readonly api: ProviderUsageApi,
    private readonly workspaceId: string,
  ) {
    forgetOldSwitches(Date.now());
    const known = lastKnown.get(workspaceId);
    if (known) {
      this.computers = known.computers;
      this.canManageOrgKeys = known.canManageOrgKeys;
      this.loaded = true;
    }
  }

  private remember(): void {
    lastKnown.set(this.workspaceId, { computers: this.computers, canManageOrgKeys: this.canManageOrgKeys }); // prettier-ignore
  }

  /** The page's first read: shows the last known state at once, then re-reads every online computer with no click. */
  private opening = false;
  open(opts: { includeOrganization?: boolean } = {}): Promise<void> {
    this.opening = true; // the re-read follows the first list that arrives, a retry's included
    return this.load(opts);
  }

  providersOf(daemonId: string): Array<{ id: string; label: string }> | undefined {
    return this.providers[daemonId];
  }

  // What each online computer runs, for the "No usage yet" rows; a failed read leaves them out.
  private async readProviders(): Promise<void> {
    const list = this.api.listProviders;
    if (!list) return;
    for (const c of this.computers.filter((x) => x.online)) {
      try {
        const found = await list.call(this.api, {
          workspaceId: this.workspaceId,
          daemonId: c.daemonId,
        });
        this.providers[c.daemonId] = found.filter((p) => p.available !== false).map((p) => ({ id: p.id, label: p.label })); // prettier-ignore
      } catch (err) {
        reportUiFailure(`${SOURCE}.providers`, err, {
          workspaceId: this.workspaceId,
          daemonId: c.daemonId,
        });
      }
    }
  }

  private switchKey(daemonId: string): string {
    return `${this.workspaceId}:${daemonId}`;
  }

  async load(opts: { daemonId?: string; includeOrganization?: boolean } = {}): Promise<void> {
    this.lastLoad = opts;
    return this.startLoad(false);
  }

  private startLoad(clearOnFailure: boolean): Promise<void> {
    const { includeOrganization, ...computers } = this.lastLoad;
    const read = this.fetchLatest(computers, clearOnFailure, true);
    if (includeOrganization) void this.fetchOrganization(true);
    return read;
  }

  // Only the newest request may write: an older answer arriving late must not bring back a revoked computer.
  private async fetchLatest(
    opts: { daemonId?: string },
    clearOnFailure: boolean,
    retry: boolean,
  ): Promise<void> {
    const id = ++this.loadSeq;
    clearTimeout(this.retryTimer);
    clearTimeout(this.deadlineTimer);
    let failed = false;
    const fail = (err: unknown) => {
      failed = true;
      clearTimeout(this.deadlineTimer);
      reportUiFailure(`${SOURCE}.load`, err, { workspaceId: this.workspaceId });
      this.loadFailed = true;
      if (clearOnFailure) this.computers = [];
      if (retry) this.retryTimer = setTimeout(() => void this.fetchLatest(opts, clearOnFailure, false), RELOAD_RETRY_MS); // prettier-ignore
      this.loaded = true;
      this.now = Date.now();
    };
    this.deadlineTimer = setTimeout(
      () => fail(new Error("cyborg:fetch_provider_usage: no answer in time")),
      LOAD_VISIBLE_MS,
    );
    try {
      const res = await this.api.fetchProviderUsage({ workspaceId: this.workspaceId, ...opts });
      if (id !== this.loadSeq) return;
      clearTimeout(this.deadlineTimer);
      clearTimeout(this.retryTimer);
      // A cold relay row (nothing mirrored) never replaces rows this page already holds.
      const held = new Map(this.computers.map((c) => [c.daemonId, c]));
      this.computers = res.computers.map((c) => {
        const prev = held.get(c.daemonId);
        const cold = c.dataAsOf === null && c.accounts.length === 0;
        if (prev && cold && prev.dataAsOf !== null) return { ...c, accounts: prev.accounts, dataAsOf: prev.dataAsOf, facts: prev.facts }; // prettier-ignore
        return { ...c, accounts: sortAccounts(c.accounts) };
      });
      this.canManageOrgKeys = res.canManageOrgKeys;
      this.remember();
      if (this.opening) {
        this.opening = false;
        this.refreshAll();
      }
      this.loadFailed = false;
      this.loaded = true;
      this.now = Date.now();
      void this.readProviders();
    } catch (err) {
      // intentional: the deadline already reported this read, or a newer read owns the page.
      if (id !== this.loadSeq || failed) return;
      fail(err);
    }
  }

  private async fetchOrganization(retry: boolean): Promise<void> {
    const id = ++this.orgSeq;
    clearTimeout(this.orgRetryTimer);
    this.orgLoading = true;
    this.orgFailed = false;
    try {
      const res = await this.api.fetchProviderUsage({
        workspaceId: this.workspaceId,
        includeOrganization: true,
        organizationOnly: true,
      });
      if (id !== this.orgSeq) return;
      this.organization = res.organization ?? [];
      this.orgLoading = false;
    } catch (err) {
      reportUiFailure(`${SOURCE}.load_organization`, err, { workspaceId: this.workspaceId });
      if (id !== this.orgSeq) return;
      if (retry) {
        this.orgRetryTimer = setTimeout(() => void this.fetchOrganization(false), RELOAD_RETRY_MS);
        return;
      }
      this.orgLoading = false;
      this.orgFailed = true;
    }
  }

  /** Re-reads what the relay shows now, with the last load's options (an access change, a refusal). */
  reload(): Promise<void> {
    return this.startLoad(true);
  }

  /** The person's Try again: the page goes back to loading rather than sitting on the failure. */
  retryLoad(): Promise<void> {
    this.loaded = false;
    this.loadFailed = false;
    return this.reload();
  }

  retryOrganization(): void {
    void this.fetchOrganization(false);
  }

  /** Ticks `now`; returns the page's disposer, which also drops a pending reload retry. */
  startClock(): () => void {
    const tick = setInterval(() => (this.now = Date.now()), CLOCK_MS);
    liveStates.add(this);
    return () => {
      liveStates.delete(this);
      clearInterval(tick);
      clearTimeout(this.retryTimer);
      clearTimeout(this.deadlineTimer);
      clearTimeout(this.orgRetryTimer);
    };
  }

  stateOf(c: UsageComputer): RefreshState {
    if (!c.online) return { kind: "offline" };
    const s = this.refreshStates[c.daemonId];
    if (s && s.kind !== "idle") return s;
    return c.dataAsOf === null ? { kind: "idle" } : { kind: "updated", at: c.dataAsOf };
  }

  private set(daemonId: string, s: RefreshState): void {
    this.refreshStates[daemonId] = s;
  }

  private apply(
    res: Pick<ProviderUsageRefreshResponse, "daemonId" | "accounts" | "facts" | "observedAt">,
  ): void {
    this.computers = this.computers.map((c) =>
      c.daemonId === res.daemonId
        ? { ...c, online: true, accounts: sortAccounts(res.accounts), facts: res.facts, dataAsOf: res.observedAt } // prettier-ignore
        : c,
    );
    this.remember();
  }

  switchOf(daemonId: string): SwitchState | undefined {
    return switchStates[this.switchKey(daemonId)];
  }

  cancelSwitch(daemonId: string): void {
    switchStates[this.switchKey(daemonId)] = undefined;
  }

  /** Asks the computer to switch. It answers with a question when agents are running and this isn't confirmed. */
  async switchAccount(c: UsageComputer, a: UsageAccount, confirmed: boolean): Promise<void> {
    const key = this.switchKey(c.daemonId);
    const current = switchStates[key];
    if (current?.kind === "checking" || current?.kind === "switching") return;
    if (!a.switcher) return;
    const who = { accountKey: a.accountKey, name: switchAccountName(a), provider: a.provider, switcher: a.switcher }; // prettier-ignore
    settledAt.delete(key);
    const epoch = signOutEpoch;
    switchStates[key] = { ...who, kind: confirmed ? "switching" : "checking" };
    const label = setTimeout(() => {
      if (switchStates[key]?.kind === "checking") switchStates[key] = { ...who, kind: "switching" };
    }, SWITCHING_LABEL_AFTER_MS);
    try {
      const request = { workspaceId: this.workspaceId, daemonId: c.daemonId, accountKey: a.accountKey, confirmed }; // prettier-ignore
      const res = await this.api.switchProviderAccount(request);
      if (epoch === signOutEpoch) this.settleSwitch(key, who, res, confirmed);
    } catch (err) {
      const failureKind = switchFailureKind(err);
      reportUiFailure(`${SOURCE}.switch`, err, { workspaceId: this.workspaceId, daemonId: c.daemonId }, failureKind ? { failureKind } : {}); // prettier-ignore
      if (epoch !== signOutEpoch) return;
      if (failureKind === "refusal") this.dropComputer(c.daemonId);
      switchStates[key] = { ...who, kind: "failed", reason: "could_not_switch", active: null, answered: false, unchanged: false, confirmed }; // prettier-ignore
      settledAt.set(key, Date.now());
      for (const page of this.pages()) void page.reload();
    } finally {
      clearTimeout(label);
    }
  }

  // Whichever pages of this workspace are showing now, this one included.
  private pages(): ProviderUsageState[] {
    const open = [...liveStates].filter((s) => s.workspaceId === this.workspaceId);
    return open.includes(this) ? open : [...open, this];
  }

  private settleSwitch(
    key: string,
    who: Pick<SwitchState, "accountKey" | "name" | "provider" | "switcher">,
    res: ProviderAccountSwitchResponse,
    confirmed: boolean,
  ): void {
    if (res.state === "needs_confirm") {
      switchStates[key] = { ...who, kind: "confirm", runningAgents: res.runningAgents };
      return;
    }
    // A switch that stood but whose re-read failed: the rows are the old ones, so none of this
    // provider's is shown Active (as for the failed answer), and the page re-reads.
    const unread = res.state !== "failed" && res.snapshotStale === true;
    const rows = unread ? res.accounts.map((a) => (a.provider === who.provider ? { ...a, active: false } : a)) : res.accounts; // prettier-ignore
    for (const page of this.pages()) {
      page.apply({ ...res, accounts: rows });
      page.now = Date.now();
      if (unread) void page.reload();
    }
    settledAt.set(key, Date.now());
    if (res.state === "failed") {
      // Last known rows after a failed re-read: naming an active account would be a guess.
      const active = res.snapshotStale ? undefined : res.accounts.find((a) => a.provider === who.provider && a.active); // prettier-ignore
      switchStates[key] = {
        ...who,
        kind: "failed",
        reason: res.reason ?? "could_not_switch",
        active: active && active.accountKey !== who.accountKey ? switchAccountName(active) : null, // null: nobody to name
        answered: true,
        unchanged: res.unchanged,
        confirmed,
      };
    } else {
      switchStates[key] = { ...who, kind: "done", runningAgents: res.runningAgents, writeUnknown: res.writeUnknown, ...(res.stillUsing ? { stillUsing: res.stillUsing } : {}) }; // prettier-ignore
    }
  }

  // A computer the viewer can no longer use is not shown at all; the re-read confirms it.
  private dropComputer(daemonId: string): void {
    this.computers = this.computers.filter((c) => c.daemonId !== daemonId);
    this.remember();
    this.set(daemonId, { kind: "idle" });
  }

  /** undefined: not counted (already in flight). */
  async refresh(daemonId: string): Promise<RefreshOutcome | undefined> {
    if (this.refreshStates[daemonId]?.kind === "refreshing") return undefined;
    this.set(daemonId, { kind: "refreshing" });
    try {
      const res = await this.api.refreshProviderUsage({ workspaceId: this.workspaceId, daemonId });
      this.apply(res);
      this.now = Date.now();
      this.set(daemonId, { kind: "updated", at: this.now });
      return "updated";
    } catch (err) {
      const outcome = outcomeOf(err);
      reportUiFailure(`${SOURCE}.refresh`, err, { workspaceId: this.workspaceId, daemonId });
      if (outcome === "refused") {
        this.dropComputer(daemonId);
        void this.reload();
        return "refused";
      }
      this.computers = this.computers.map((c) =>
        c.daemonId === daemonId && outcome === "offline" ? { ...c, online: false } : c,
      );
      this.remember();
      this.set(daemonId, { kind: outcome === "offline" ? "offline" : "no_answer" });
      return outcome;
    }
  }

  // Starts every online computer at once; each card settles on its own. The aggregate KPI is sent
  // when the last one settles, and nothing on screen waits for it.
  refreshAll(): void {
    if (this.refreshingAll) return;
    this.refreshingAll = true;
    void this.refreshOnline().finally(() => (this.refreshingAll = false));
  }

  private async refreshOnline(): Promise<void> {
    const online = this.computers.filter((c) => c.online);
    const settled = await Promise.all(online.map((c) => this.refresh(c.daemonId)));
    const results = settled.filter((r): r is RefreshOutcome => r !== undefined);
    const kpi = refreshAllKpi(results);
    if (!kpi) return;
    reportKpi("provider_usage.refresh_all", { workspaceId: this.workspaceId, ...kpi });
  }
}
